import { Eye, EyeOff, X } from "lucide-react";
import { forwardRef, useState, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import { Input } from "@/components/ui/v2/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/v2/tooltip";

type SecretInputProps = Pick<ComponentProps<typeof Input>, "id" | "name" | "placeholder" | "disabled" | "autoFocus" | "onBlur"> & {
  value: string;
  onChange: (next: string) => void;
  onUndo?: () => void;
};

export const SecretInput = forwardRef<HTMLInputElement, SecretInputProps>(
  ({ value, onChange, onUndo, ...inputProps }, ref) => {
    const { t } = useTranslation();
    const [showSecret, setShowSecret] = useState(false);
    const undoLabel = t("integrations.modules.configure.fields.undoSecretChange");

    return (
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <Input
            {...inputProps}
            ref={ref}
            autoComplete="off"
            type={showSecret ? "text" : "password"}
            className="pr-10"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowSecret(!showSecret);
            }}
            className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={t(
              showSecret
                ? "integrations.modules.configure.fields.hideSecret"
                : "integrations.modules.configure.fields.showSecret",
            )}
            tabIndex={-1}
          >
            {showSecret ? <EyeOff /> : <Eye />}
          </Button>
        </div>
        {onUndo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                aria-label={undoLabel}
                onClick={onUndo}
              >
                <X />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{undoLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  },
);

SecretInput.displayName = "SecretInput";
